import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { TaskListDefinition, SavedChecklist, SavedAudit } from '@techgitter/contracts';
import type { WorkItem } from '../workitems/workItemTypes';
import type { LiveChecklist } from '../../types/taskList';
import { useWorkItemChecklist } from '../../hooks/useWorkItemChecklist';
import { useTaskLists } from '../../hooks/useTaskLists';
import { useTaskDropdowns } from '../../hooks/useTaskDropdowns';
import { ChecklistPanel } from '../tickets/ChecklistPanel';
import { TaskListPicker } from '../tickets/TaskListPicker';
import { resolveStandaloneTaskList } from '../../utils/checklistEngine';
import {
  collectReferencedContextKeys,
  getMissingContextKeys,
  type MissingContextKeys,
} from '../../utils/taskListContextRequirements';
import {
  TaskListContextPromptDialog,
  type TaskListContextValues,
} from '../tickets/TaskListContextPromptDialog';
import './WorkItemPanel.css';

interface TaskListSectionProps {
  workItem: WorkItem;
  userName: string;
  onWorkItemUpdated: (id: string, updates: Partial<WorkItem>) => void;
  /** Increment to open the task-list picker from the panel's + button. */
  addSignal?: number;
  /** Reports the current task progress (total/closed) up to the panel header. */
  onSummaryChange?: (summary: { total: number; closed: number }) => void;
}

/**
 * Pick the next composite-ID suffix for a task list that may already be
 * attached — mirrors WorkItemExpansionPanel.nextAttachmentIndex so the same
 * list can be attached more than once with unique section/task IDs.
 */
function nextAttachmentIndex(checklist: LiveChecklist | null, taskListId: string): number {
  if (!checklist) return 0;
  let max = -1;
  for (const s of checklist.sections) {
    if (s.sourceTaskListId !== taskListId) continue;
    const parts = s.sectionId.split('--');
    const suffix = parts.length > 1 ? Number(parts[parts.length - 1]) : 0;
    if (Number.isFinite(suffix) && suffix > max) max = suffix;
  }
  return max + 1;
}

/**
 * Full task-list picker + checklist editor for the V2 work-item panel. Ports
 * the legacy WorkItemExpansionPanel behaviour: attach a task list (prompting
 * for any condition-referenced context it needs), render the shared
 * ChecklistPanel, and save the checklist + audit back onto the work item so the
 * row's task-progress chip updates.
 */
export const TaskListSection: React.FC<TaskListSectionProps> = ({
  workItem,
  userName,
  onWorkItemUpdated,
  addSignal,
  onSummaryChange,
}) => {
  const jobGuid = workItem.jobGUID ?? workItem.guid;

  const handleChecklistSaved = useCallback(
    (savedChecklist: SavedChecklist, savedAudit: SavedAudit) => {
      onWorkItemUpdated(workItem.id, { taskList: savedChecklist, taskAudit: savedAudit });
    },
    [onWorkItemUpdated, workItem.id],
  );

  const { dropdownCatalog } = useTaskDropdowns();
  const {
    checklist,
    progress,
    isDirty,
    isSaving,
    saveError,
    appendChecklist,
    onSetStatus,
    onSetNote,
    onSetOption,
    onRemoveTask,
    onRestoreTask,
    onRemoveSection,
    onRestoreSection,
    onAddManualTask,
    onSave,
  } = useWorkItemChecklist({
    jobGuid,
    savedChecklist: workItem.taskList,
    savedAudit: workItem.taskAudit,
    userName,
    taskDropdowns: dropdownCatalog.dropdowns,
    onSaved: handleChecklistSaved,
  });

  const { taskListCatalog, isLoading: isLoadingTaskLists } = useTaskLists();
  const [showPicker, setShowPicker] = useState(false);
  const [pendingAttach, setPendingAttach] = useState<{
    def: TaskListDefinition;
    missing: MissingContextKeys;
  } | null>(null);

  const attachResolvedTaskList = useCallback(
    (
      taskListDef: TaskListDefinition,
      additionalDetails: Record<string, string | number | boolean>,
      customer: Record<string, string | number | boolean>,
    ) => {
      // Pass the full catalog so nested TaskListReferences can be expanded.
      const attachmentIndex = nextAttachmentIndex(checklist, taskListDef.taskListId);
      const sections = resolveStandaloneTaskList(
        taskListDef,
        attachmentIndex,
        additionalDetails,
        customer,
        taskListCatalog.taskLists,
      );
      appendChecklist({ sections });
      setShowPicker(false);
    },
    [appendChecklist, taskListCatalog, checklist],
  );

  // Attach a task list, prompting first for any condition-referenced context
  // that isn't in scope (the panel has no template/customer context, so any
  // referenced key is treated as missing — same as the legacy panel).
  const handleAttachTaskList = useCallback(
    (taskListDef: TaskListDefinition) => {
      const referenced = collectReferencedContextKeys(taskListDef, taskListCatalog.taskLists);
      const missing = getMissingContextKeys(referenced, {}, {});
      if (missing.fieldValueKeys.length === 0 && missing.customerContextKeys.length === 0) {
        attachResolvedTaskList(taskListDef, {}, {});
        return;
      }
      setPendingAttach({ def: taskListDef, missing });
    },
    [attachResolvedTaskList, taskListCatalog],
  );

  const handleContextSubmit = useCallback(
    (values: TaskListContextValues) => {
      if (!pendingAttach) return;
      attachResolvedTaskList(pendingAttach.def, values.fieldValues, values.customerContextValues);
      setPendingAttach(null);
    },
    [pendingAttach, attachResolvedTaskList],
  );

  // External "+" trigger from the panel header — opens the picker.
  const lastAddSignal = useRef(addSignal);
  useEffect(() => {
    if (addSignal === undefined || addSignal === lastAddSignal.current) return;
    lastAddSignal.current = addSignal;
    setShowPicker(true);
  }, [addSignal]);

  // Report progress up to the panel header summary.
  useEffect(() => {
    onSummaryChange?.({ total: progress.total, closed: progress.closed });
  }, [progress.total, progress.closed, onSummaryChange]);

  return (
    <div className="task-list-section">
      {showPicker && (
        <div className="task-list-section__picker">
          <TaskListPicker
            availableLists={taskListCatalog.taskLists}
            isLoading={isLoadingTaskLists}
            onAttach={handleAttachTaskList}
          />
          <button
            type="button"
            className="task-list-section__picker-cancel"
            onClick={() => setShowPicker(false)}
          >
            Cancel
          </button>
        </div>
      )}

      {!checklist && !showPicker && (
        <div className="line-items__hint">No task lists</div>
      )}

      {checklist && (
        <>
          <ChecklistPanel
            checklist={checklist}
            progress={progress}
            disabled={isSaving}
            taskDropdowns={dropdownCatalog.dropdowns}
            onSetStatus={onSetStatus}
            onSetNote={onSetNote}
            onSetOption={onSetOption}
            onRemoveTask={onRemoveTask}
            onRestoreTask={onRestoreTask}
            onRemoveSection={onRemoveSection}
            onRestoreSection={onRestoreSection}
            onAddManualTask={onAddManualTask}
          />
          {saveError && <div className="line-items__error">{saveError}</div>}
          <div className="line-items__save">
            <button
              type="button"
              className="save-btn"
              onClick={onSave}
              disabled={!isDirty || isSaving}
            >
              {isSaving ? 'Saving…' : 'Save Checklist'}
            </button>
          </div>
        </>
      )}

      {pendingAttach && (
        <TaskListContextPromptDialog
          open
          taskListLabel={pendingAttach.def.displayName}
          missingFieldValueKeys={pendingAttach.missing.fieldValueKeys}
          missingCustomerContextKeys={pendingAttach.missing.customerContextKeys}
          onCancel={() => setPendingAttach(null)}
          onSubmit={handleContextSubmit}
        />
      )}
    </div>
  );
};
